import React, { useCallback, useEffect, useRef, useState } from "react";
import css from "./List.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import update from "immutability-helper";
import { DatabaseTypes } from "./DatabaseTypes";
import { useSearch } from "../../../hooks/search";
import Spinner from "../../Spinner";
import { useStarterPathway } from "../../../hooks/start";
import { useSession } from "../../../hooks/session";
import { styleMsgDiv_MSGOIN, styleMsgDiv_MSGOUT } from "./styleMessages";
import {
  minimumChatacters,
  warningSearchMessage,
} from "../../warningSearchMessage";

const ListPathway = ({ sessionId, term, setTerm }) => {
  const { data, isLoading } = useSession(sessionId);

  const [species, setSpecies] = useState(isLoading ? "" : data.species),
    [showWarning, setShowWarning] = useState(false),
    [showSpinnerFetch, setShowSpinnerFetch] = useState(false),
    [debouncedTerm, setDebouncedTerm] = useState(term),
    { dataRetrieved, isLoadingSearch } = useSearch(term, species);

  const setTermWithWarning = () => {
    if (debouncedTerm.length <= minimumChatacters && debouncedTerm !== "") {
      setShowWarning(true);
    } else {
      setShowWarning(false);
      setTerm(debouncedTerm);
    }
  };

  useEffect(() => {
    setShowSpinnerFetch(false);
  }, [sessionId]);

  useEffect(() => {
    const timer = setTimeout(() => setTermWithWarning(), 1000);
    return () => clearTimeout(timer);
  }, [debouncedTerm]);

  return (
    <div className={css.listComponent}>
      <div className={css.searchGrid}>
        <div className={css.search}>
          <input
            value={debouncedTerm}
            className={css.inputSearch}
            type="text"
            placeholder="Search for pathways title..."
            onChange={(e) => setDebouncedTerm(e.target.value)}
          />
        </div>
        <div style={showWarning ? styleMsgDiv_MSGOIN : styleMsgDiv_MSGOUT}>
          {warningSearchMessage}
        </div>
      </div>
      <ListContainer
        species={species}
        setSpecies={setSpecies}
        dataRetrieved={dataRetrieved}
        isLoadingSearch={isLoadingSearch}
        setShowSpinnerFetch={setShowSpinnerFetch}
      />
      {showSpinnerFetch ? (
        <div
          style={{
            height: "100%",
            width: "100%",
            zIndex: "10",
            placeSelf: "center",
            gridRow: "1/3",
            gridColumn: "1/2",
            backgroundColor: "rgba(255,255,255,0.5)",
          }}
        >
          <Spinner size={"2x"} />
        </div>
      ) : null}
    </div>
  );
};
export default ListPathway;

const ListContainer = ({
  species,
  setSpecies,
  dataRetrieved,
  isLoadingSearch,
  setShowSpinnerFetch,
}) => {
  const ListSection = () =>
    isLoadingSearch ? (
      <Spinner />
    ) : (
      <ListDatabases
        itemsSpecies={dataRetrieved.items[species]}
        status={dataRetrieved.status}
        species={species}
        setShowSpinnerFetch={setShowSpinnerFetch}
      />
    );

  return (
    <div className={css.listContainer}>
      <div className={css.buttonContainer}>
        Species:{" "}
        <select
          value={species}
          onChange={(event) => setSpecies(event.currentTarget.value)}
        >
          {speciesList.map((spc) => {
            return (
              <option value={spc} key={spc}>
                {`${spc[0].toUpperCase()}. ${spc.slice(1)}`}
              </option>
            );
          })}
        </select>
      </div>
      <ListSection />
    </div>
  );
};

const ListDatabases = ({
  itemsSpecies,
  status,
  species,
  setShowSpinnerFetch,
}) => {
  const [dbList, setDbList] = useState(itemsSpecies);

  useEffect(() => {
    setDbList(itemsSpecies);
  }, [itemsSpecies]);

  const moveDB = useCallback((dragIndex, hoverIndex) => {
    setDbList((prevCards) =>
      update(prevCards, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prevCards[dragIndex]],
        ],
      })
    );
  }, []);

  const renderBoxDb = (db, index) => {
    return (
      <BoxDb
        key={db.name}
        dbtitle={db.name}
        dblist={db.data}
        species={species}
        index={index}
        id={db.id}
        moveDB={moveDB}
        initialToggleBox={register[species][db.name]}
        setShowSpinnerFetch={setShowSpinnerFetch}
      />
    );
  };

  if (status) {
    return (
      <DndProvider backend={HTML5Backend}>
        <div className={css.list}>
          <div className={css.containerBox}>
            {dbList.map((db, index) => renderBoxDb(db, index))}
          </div>
        </div>
      </DndProvider>
    );
  } else {
    return (
      <div className={css.list}>
        <div className={css.containerBox}>
          <div className={css.boxNoResults}>
            <div style={{ placeSelf: "center" }}>
              No results.
            </div>
          </div>
        </div>
      </div>
    );
  }
};

const BoxDb = ({
  dbtitle,
  dblist,
  species,
  index,
  id,
  moveDB,
  initialToggleBox,
  setShowSpinnerFetch,
}) => {
  const [activeBox, activateBox] = useState(initialToggleBox);

  function toggleBox() {
    setOpenDB(species, dbtitle);
    activateBox(!activeBox);
  }

  const ref = useRef(null);
  const [{ handlerId }, drop] = useDrop({
    accept: DatabaseTypes.DATABASE,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      // Time to actually perform the action
      moveDB(dragIndex, hoverIndex);
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: DatabaseTypes.DATABASE,
    item: () => {
      return { id, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drag(drop(ref));

  return (
    <div
      className={`${css.boxdb} ${dblist.length === 0 ? css.invisible : ""}`}
      ref={ref}
      data-handler-id={handlerId}
    >
      <BoxTitle title={dbtitle} onClick={toggleBox} open={activeBox} />
      <BoxList
        dblist={dblist}
        activeBox={activeBox}
        setShowSpinnerFetch={setShowSpinnerFetch}
      />
    </div>
  );
};

const BoxTitle = ({ title, onClick, open }) => {
  return (
    <div className={css.boxtitle}>
      <div className={css.titleText}>{title}</div>
      <FontAwesomeIcon
        className={css.titleIcon1}
        icon={open ? "angle-up" : "angle-down"}
        onClick={onClick}
        style={{ cursor: "pointer" }}
      />
    </div>
  );
};

const BoxList = ({ dblist, activeBox, setShowSpinnerFetch }) => {
  const { startPathway } = useStarterPathway();

  let display;
  activeBox ? (display = "block") : (display = "none");
  let hgt = dblist.length < 4 ? (dblist.length - 1) * 4 + 3.75 : 15;

  // TODO: consider removing List Component
  return (
    <div
      className={css.boxListRelative}
      style={{ display: display, height: `${hgt}rem` }}
    >
      <div className={css.boxListAbsolute}>
        <ul>
          {dblist.map((item) => (
            <li key={item.id}>
              <div
                className={css.pathwayEntry}
                onClick={() => {
                  setShowSpinnerFetch(true);
                  startPathway(item.species, item.database, item.nativeId);
                }}
                style={{
                  paddingTop: "10px",
                  paddingLeft: "5px",
                  hover: { color: "red" },
                }}
                title={`Nodes: ${item.num_nodes}\nEdges: ${item.num_edges}`}
              >
                {item.title}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export const speciesList = [
  "athaliana",
  "btaurus",
  "celegans",
  "cfamiliaris",
  "dmelanogaster",
  "drerio",
  "ecoli",
  "ggallus",
  "hsapiens",
  "mmusculus",
  "rnorvegicus",
  "scerevisiae",
  "sscrofa",
  "xlaevis",
];

let register = {
  athaliana: { kegg: false, pathbank: false },
  btaurus: { kegg: false, pathbank: false, reactome: false },
  celegans: { kegg: false, pathbank: false, reactome: false },
  cfamiliaris: { kegg: false, reactome: false },
  dmelanogaster: { kegg: false, pathbank: false, reactome: false },
  drerio: { kegg: false, reactome: false },
  ecoli: { kegg: false, pathbank: false },
  ggallus: { kegg: false, reactome: false },
  hsapiens: {
    kegg: false,
    panther: false,
    pathbank: false,
    pharmgkb: false,
    reactome: false,
    smpdb: false,
  },
  mmusculus: { kegg: false, pathbank: false, reactome: false },
  rnorvegicus: { kegg: false, pathbank: false, reactome: false },
  scerevisiae: { kegg: false, pathbank: false, reactome: false },
  sscrofa: { kegg: false, reactome: false },
  xlaevis: { kegg: false },
};

const setOpenDB = (species, dbname) => {
  let oldValue = register[species][dbname];
  register[species][dbname] = !oldValue;
};
